import React, { useCallback, useEffect, useState } from 'react'
import { Card, Container, HeaderBar, Tabs } from 'components'
import { CircularProgress } from '@mui/material'
import { getCompanyMonitoring, getFleets, getRecipientCandidates, getValidationTypes } from 'api'
import {
  ICompanyMonitoringData,
  IFleetData,
  IRecipientCandidate,
  IValidationTypeData,
} from 'interfaces/IFleetMonitoring'
import strings from 'strings'
import OnboardingView from './onboardingView'
import FleetsTab from './fleetsTab'
import AlertSettingsTab from './alertSettingsTab'
import AlertFeedTab from './alertFeedTab'
import styles from './fleetMonitoring.module.scss'

const FM = strings.FLEET_MONITORING

const TAB_DATA = [
  { id: 'fleets', label: 'Fleets', dataTestId: 'fleet-monitoring-tab-fleets' },
  { id: 'settings', label: 'Alert Settings', dataTestId: 'fleet-monitoring-tab-settings' },
  { id: 'alerts', label: FM.ALERT_FEED.TAB, dataTestId: 'fleet-monitoring-tab-alerts' },
]

export type SetupState = 'ok' | 'skipped' | 'paused' | 'no_alerts_enabled'

const computeSetupState = (data: ICompanyMonitoringData | null): SetupState => {
  if (!data) return 'ok'
  const types = data.validationTypes || []
  if (!data.active) return 'paused'
  if (!types.some(t => t.enabled)) return 'no_alerts_enabled'
  return 'ok'
}

const FleetMonitoring: React.FunctionComponent = () => {
  const [loading, setLoading] = useState(true)
  const [companyData, setCompanyData] = useState<ICompanyMonitoringData | null>(null)
  const [fleets, setFleets] = useState<IFleetData[]>([])
  const [validationTypes, setValidationTypes] = useState<IValidationTypeData[]>([])
  const [candidates, setCandidates] = useState<IRecipientCandidate[]>([])
  const [tabValue, setTabValue] = useState(0)
  const [justSkipped, setJustSkipped] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [dismissedBanner, setDismissedBanner] = useState<SetupState | null>(null)
  /** True once the company endpoint has returned real data at least once. Drives
   *  the onboarding vs. tabs decision so a failed call can't flip us back to
   *  onboarding mid-session (e.g. when the global axios interceptor swallows a 401). */
  const [companyLoaded, setCompanyLoaded] = useState(false)

  const loadFleets = useCallback(async () => {
    try {
      const data = await getFleets()
      setFleets(data || [])
    } catch {
      // Global request interceptor already toasts the failure — keep the table empty.
      setFleets([])
    }
  }, [])

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const [company, , types, candidateList] = await Promise.all([
        getCompanyMonitoring(),
        loadFleets(),
        getValidationTypes(),
        getRecipientCandidates(),
      ])
      // Interceptor can resolve a 401 with `undefined`; only commit real data.
      if (company) {
        setCompanyData(company)
        setCompanyLoaded(true)
      }
      if (types) {
        setValidationTypes(types)
      }
      if (candidateList) {
        setCandidates(candidateList)
      }
    } catch {
      // Global request interceptor already toasts the failure. Keep prior data if any.
    } finally {
      setLoading(false)
    }
  }, [loadFleets])

  const refreshFleetsView = useCallback(async () => {
    setRefreshing(true)
    try {
      const [company] = await Promise.all([getCompanyMonitoring(), loadFleets()])
      if (company) {
        setCompanyData(company)
        setCompanyLoaded(true)
      }
    } catch {
      // Global interceptor toast handles it.
    } finally {
      setRefreshing(false)
    }
  }, [loadFleets])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const renderBody = () => {
    if (loading) {
      return (
        <Card>
          <div className={styles.loader}>
            <CircularProgress />
            <span className={styles.loaderLabel}>{FM.COMMON.LOADING}</span>
          </div>
        </Card>
      )
    }

    // Only fall back to onboarding when the company endpoint actually responded
    // and said "not enrolled". A failed load (e.g. session kicked → 401) keeps
    // us on the tabs view with whatever data we have so we never flash the
    // onboarding screen on top of an in-flight session expiry.
    const isEnrolled = companyData?.companyMonitoringId != null
    if (companyLoaded && !isEnrolled) {
      const handleEnrolled = (opts?: { skipped?: boolean }) => {
        setJustSkipped(!!opts?.skipped)
        loadAll()
      }
      return <OnboardingView onEnrolled={handleEnrolled} candidates={candidates} />
    }

    const setupState: SetupState = justSkipped ? 'skipped' : computeSetupState(companyData)

    const handleGoToSettings = () => {
      setJustSkipped(false)
      setTabValue(1)
    }

    const handleTabChange = (_e: React.SyntheticEvent, v: number) => {
      if (v === 0 && tabValue !== 0) {
        refreshFleetsView()
      }
      setTabValue(v)
    }

    return (
      <>
        <Tabs tabData={TAB_DATA} value={tabValue} handleChange={handleTabChange} />
        <div className={styles.tabPanel}>
          {tabValue === 0 ? (
            <FleetsTab
              fleets={fleets}
              validationTypes={validationTypes}
              candidates={candidates}
              onReload={loadFleets}
              setupState={setupState}
              onGoToSettings={handleGoToSettings}
              refreshing={refreshing}
              dismissedBanner={dismissedBanner}
              onDismissBanner={() => setDismissedBanner(setupState)}
            />
          ) : tabValue === 2 ? (
            <AlertFeedTab />
          ) : (
            <AlertSettingsTab candidates={candidates} />
          )}
        </div>
      </>
    )
  }

  return (
    <Container data-testid="fleet-monitoring-page">
      <HeaderBar title={strings.MENU.FLEET_MONITORING} />
      {renderBody()}
      {!loading && (
        <p className={styles.disclaimer} data-testid="fleet-monitoring-disclaimer">
          {FM.COMMON.DISCLAIMER}
        </p>
      )}
    </Container>
  )
}

export default FleetMonitoring
